<script lang="ts" setup>
import TriStateCheckbox from "@/components/common/TriStateCheckbox.vue";
import { currencySymbol } from "@/util/currencySymbol";
import {
  PAYMENT_SECTION_KEY,
  clearProductSelection,
  countSelectedProducts,
  countTotalProducts,
  isEntireCatalogSelected,
  keysToProductSelection,
  productSelectionToKeys,
  removeUnknownProductKeys,
  resolveProductCheckState,
  selectAllProducts,
  setBumpSelected,
  setProductSelected,
  setVariantSelected,
  variantSelectionId,
  type BumpCatalogEntry,
  type ProductCatalog,
  type ProductCatalogEntry,
} from "@/util/product-conditional-logic";
import {
  AlertTriangleIcon,
  ChevronRightIcon,
  Cube01Icon,
  InfoCircleIcon,
  SearchMdIcon,
} from "@gohighlevel/ghl-icons/24/outline";
import { computed, ref, watch } from "vue";
import { useI18n } from "vue-i18n";

const { t } = useI18n();

const props = defineProps<{
  /**
   * The payment-related slice of `outcome.value`: either `["payment"]` for the whole
   * section, or the `product::` / `bump::` keys for individual targets. Never contains
   * plain field keys — the parent owns those.
   */
  modelValue: string[];
  catalog: ProductCatalog;
  /** Label of the payment element itself ("Sell Products" / "Collect Payment"). */
  sectionLabel: string;
  /** `Hide` / `Hide Multiple` / `Show` / `Show Multiple` — only drives the hint copy. */
  hideType: string;
  /**
   * False while the product catalog request is still in flight. Until it resolves every
   * saved key looks unresolvable, so the "no longer exists" warning must stay silent.
   */
  catalogLoaded: boolean;
}>();

const emit = defineEmits<{
  (event: "update:modelValue", value: string[]): void;
}>();

type PickerMode = "entire" | "specific";

const resolveInitialMode = (value: string[]): PickerMode =>
  value.some((key) => key !== PAYMENT_SECTION_KEY) ? "specific" : "entire";

const mode = ref<PickerMode>(resolveInitialMode(props.modelValue));
const searchTerm = ref("");
const expandedGroups = ref<Set<"main" | "bump">>(new Set(["main", "bump"]));

const selection = computed(() =>
  keysToProductSelection(props.modelValue, props.catalog)
);

// Products that already have a selection open expanded, so reopening a saved rule shows
// which variants are ticked rather than hiding them behind a collapsed row.
const expandedProductIds = ref<Set<string>>(
  new Set(
    keysToProductSelection(
      props.modelValue,
      props.catalog
    ).productVariants.keys()
  )
);

const isShowRule = computed(() => props.hideType.includes("Show"));
const isCatalogEmpty = computed(() => countTotalProducts(props.catalog) === 0);

/** With no products configured there is nothing to drill into, so only the whole section applies. */
const effectiveMode = computed<PickerMode>(() =>
  isCatalogEmpty.value ? "entire" : mode.value
);

/** Re-derive the mode when the rule is swapped underneath us (e.g. editing another rule). */
watch(
  () => props.modelValue,
  (value) => {
    const derived = resolveInitialMode(value);
    // An empty value is ambiguous, so leave whatever the merchant last picked in place.
    if (value.length > 0 && derived !== mode.value) {
      mode.value = derived;
    }
  }
);

const emitSelection = (next: ReturnType<typeof keysToProductSelection>) => {
  emit("update:modelValue", productSelectionToKeys(next, props.catalog));
};

const setMode = (next: PickerMode) => {
  if (next === "specific" && isCatalogEmpty.value) {
    return;
  }
  if (mode.value === next) {
    return;
  }
  mode.value = next;
  emit("update:modelValue", next === "entire" ? [PAYMENT_SECTION_KEY] : []);
};

const matchesSearch = (haystack: string): boolean =>
  haystack.toLowerCase().includes(searchTerm.value.trim().toLowerCase());

/** A product stays listed when its own name matches, or any of its variants does. */
const visibleProducts = computed(() =>
  props.catalog.products.filter(
    (product) =>
      !searchTerm.value.trim() ||
      matchesSearch(product.name) ||
      product.variants.some((variant) => matchesSearch(variant.name))
  )
);

const visibleBumps = computed(() =>
  props.catalog.bumps.filter(
    (bump) => !searchTerm.value.trim() || matchesSearch(bump.name)
  )
);

const hasResults = computed(
  () => visibleProducts.value.length + visibleBumps.value.length > 0
);

/** While searching, matching variants are revealed without the merchant expanding rows. */
const visibleVariants = (product: ProductCatalogEntry) => {
  const term = searchTerm.value.trim();
  if (!term || matchesSearch(product.name)) {
    return product.variants;
  }
  return product.variants.filter((variant) => matchesSearch(variant.name));
};

/** Single-variant products have nothing to drill into, so their chevron is hidden. */
const hasVariantRows = (product: ProductCatalogEntry): boolean =>
  product.variants.length > 1;

const isProductExpanded = (product: ProductCatalogEntry): boolean => {
  if (!hasVariantRows(product)) {
    return false;
  }
  const term = searchTerm.value.trim();
  if (term && !matchesSearch(product.name)) {
    return true;
  }
  return expandedProductIds.value.has(product.productId);
};

const toggleProductExpanded = (product: ProductCatalogEntry) => {
  const next = new Set(expandedProductIds.value);
  if (next.has(product.productId)) {
    next.delete(product.productId);
  } else {
    next.add(product.productId);
  }
  expandedProductIds.value = next;
};

const toggleGroupExpanded = (group: "main" | "bump") => {
  const next = new Set(expandedGroups.value);
  if (next.has(group)) {
    next.delete(group);
  } else {
    next.add(group);
  }
  expandedGroups.value = next;
};

const productChecked = (product: ProductCatalogEntry) =>
  resolveProductCheckState(product, selection.value) === "checked";

const productPartial = (product: ProductCatalogEntry) =>
  resolveProductCheckState(product, selection.value) === "indeterminate";

const variantChecked = (
  product: ProductCatalogEntry,
  priceId: string
): boolean =>
  !!selection.value.productVariants.get(product.productId)?.has(priceId);

const bumpChecked = (bump: BumpCatalogEntry): boolean =>
  selection.value.bumps.has(variantSelectionId(bump.productId, bump.priceId));

const onProductToggle = (product: ProductCatalogEntry) =>
  emitSelection(
    setProductSelected(selection.value, product, !productChecked(product))
  );

const onVariantToggle = (product: ProductCatalogEntry, priceId: string) =>
  emitSelection(
    setVariantSelected(
      selection.value,
      product,
      priceId,
      !variantChecked(product, priceId)
    )
  );

const onBumpToggle = (bump: BumpCatalogEntry) =>
  emitSelection(setBumpSelected(selection.value, bump, !bumpChecked(bump)));

const onSelectAll = () =>
  emitSelection(selectAllProducts(selection.value, props.catalog));
const onClear = () => emitSelection(clearProductSelection());
const onRemoveDeleted = () =>
  emitSelection(removeUnknownProductKeys(selection.value));

const selectedCount = computed(() =>
  countSelectedProducts(selection.value, props.catalog)
);
const totalCount = computed(() => countTotalProducts(props.catalog));

/**
 * Targets whose product is gone from the catalog. Suppressed until the catalog request
 * resolves, otherwise every saved key would look deleted while the API is in flight.
 */
const deletedCount = computed(() =>
  props.catalogLoaded && effectiveMode.value === "specific"
    ? selection.value.unknownKeys.size
    : 0
);

const formatPrice = (amount?: number, currency?: string): string =>
  typeof amount === "number"
    ? `${currencySymbol(currency || "") || ""}${amount.toLocaleString()}`
    : "";

const productName = (name: string) =>
  name || t("form.conditonalLogic.untitledProduct");

const productMeta = (product: ProductCatalogEntry): string =>
  hasVariantRows(product)
    ? t("form.conditonalLogic.variantsCount", {
        count: product.variants.length,
      })
    : formatPrice(product.variants[0]?.amount, product.variants[0]?.currency);

const entireHint = computed(() =>
  isShowRule.value
    ? t("form.conditonalLogic.productHint.entireShow", {
        section: props.sectionLabel,
      })
    : t("form.conditonalLogic.productHint.entireHide", {
        section: props.sectionLabel,
      })
);

/**
 * Footer copy, in the design's priority order: nothing picked, then "everything picked"
 * (which is the whole section by another name), then the plain description.
 */
const footerHint = computed(() => {
  if (selectedCount.value === 0) {
    return t("form.conditonalLogic.selectAtLeastOneProduct");
  }
  if (isEntireCatalogSelected(selection.value, props.catalog)) {
    return isShowRule.value
      ? t("form.conditonalLogic.productHint.allSelectedShow")
      : t("form.conditonalLogic.productHint.allSelectedHide");
  }
  return isShowRule.value
    ? t("form.conditonalLogic.productHint.specificShow")
    : t("form.conditonalLogic.productHint.specificHide");
});

const isFooterWarning = computed(
  () =>
    selectedCount.value === 0 ||
    isEntireCatalogSelected(selection.value, props.catalog)
);

const deletedTitle = computed(() =>
  deletedCount.value === 1
    ? t("form.conditonalLogic.deletedProductTitleOne")
    : t("form.conditonalLogic.deletedProductTitleOther", {
        count: deletedCount.value,
      })
);

const deletedDescription = computed(() =>
  deletedCount.value === 1
    ? t("form.conditonalLogic.deletedProductBodyOne", {
        section: props.sectionLabel,
      })
    : t("form.conditonalLogic.deletedProductBodyOther", {
        section: props.sectionLabel,
      })
);
</script>

<template>
  <div
    class="conditional-product-picker overflow-hidden rounded-md border border-gray-200 bg-white"
  >
    <!-- Header: element identity + scope switcher -->
    <div
      class="flex items-center gap-x-2.5 border-b border-gray-200 px-3 py-2.5"
    >
      <Cube01Icon class="h-[18px] w-[18px] shrink-0 text-gray-600" />
      <span class="min-w-0 flex-1 truncate text-[15px] font-semibold text-gray-900">
        {{ sectionLabel }}
      </span>
      <!--
        Matches the Then/And/Or switcher (a naive "segment" tab bar) by value. Not UITabs:
        it drops the class it is given, needs non-empty panes, and fills its container.
      -->
      <div class="flex shrink-0 rounded-md bg-[#F7F7FA] p-[3px]">
        <button
          id="conditional-product-mode-entire"
          type="button"
          class="cursor-pointer appearance-none border-0 shadow-none focus:outline-none rounded-md px-3 py-1.5 text-sm font-semibold transition-colors"
          :class="
            effectiveMode === 'entire'
              ? 'bg-white text-[rgb(51,54,57)] shadow-[0_1px_2px_0_rgb(16_24_40/5%)]'
              : 'bg-transparent text-gray-500'
          "
          @click="setMode('entire')"
        >
          {{ t("form.conditonalLogic.entireSection") }}
        </button>
        <button
          id="conditional-product-mode-specific"
          type="button"
          class="appearance-none rounded-md border-0 px-3 py-1.5 text-sm font-semibold shadow-none transition-colors focus:outline-none"
          :class="[
            effectiveMode === 'specific'
              ? 'bg-white text-[rgb(51,54,57)] shadow-[0_1px_2px_0_rgb(16_24_40/5%)]'
              : 'bg-transparent text-gray-500',
            isCatalogEmpty ? 'cursor-not-allowed opacity-50' : 'cursor-pointer',
          ]"
          :disabled="isCatalogEmpty"
          @click="setMode('specific')"
        >
          {{ t("form.conditonalLogic.specificProducts") }}
        </button>
      </div>
    </div>

    <!-- A saved target whose product has since been removed blocks saving -->
    <div
      v-if="deletedCount > 0"
      id="conditional-product-deleted-banner"
      class="mx-3 mt-3 flex gap-x-2.5 rounded-md border border-warning-200 bg-warning-50 px-3 py-2.5"
    >
      <AlertTriangleIcon
        class="mt-px h-[18px] w-[18px] shrink-0 text-warning-600"
      />
      <div class="flex min-w-0 flex-1 flex-col gap-y-0.5">
        <span class="text-sm font-semibold text-warning-700">{{
          deletedTitle
        }}</span>
        <span class="text-[13px] text-gray-700">{{ deletedDescription }}</span>
      </div>
      <button
        id="conditional-product-remove-deleted"
        type="button"
        class="shrink-0 cursor-pointer appearance-none whitespace-nowrap border-0 bg-transparent p-0 text-[13px] font-semibold text-error-600 shadow-none focus:outline-none"
        @click="onRemoveDeleted"
      >
        {{ t("form.conditonalLogic.remove") }}
      </button>
    </div>

    <!-- No products configured on the element at all -->
    <div
      v-if="isCatalogEmpty"
      class="flex flex-col items-center gap-y-1 px-4 py-6 text-center"
    >
      <span
        class="mb-1.5 flex h-9 w-9 items-center justify-center rounded-full bg-gray-100"
      >
        <Cube01Icon class="h-[18px] w-[18px] text-gray-600" />
      </span>
      <span class="text-sm font-semibold text-gray-900">
        {{ t("form.conditonalLogic.noProductsTitle") }}
      </span>
      <span class="max-w-[380px] text-[13px] text-gray-600">
        {{
          t("form.conditonalLogic.noProductsBody", { section: sectionLabel })
        }}
      </span>
    </div>

    <div
      v-else-if="effectiveMode === 'entire'"
      class="flex items-center gap-x-2 p-3 text-[13px] text-gray-600"
    >
      <InfoCircleIcon class="h-[15px] w-[15px] shrink-0" />
      <span>{{ entireHint }}</span>
    </div>

    <template v-else>
      <div class="flex items-center gap-x-3 px-3 pb-1 pt-3">
        <label
          class="flex h-9 flex-1 items-center gap-x-2 rounded border border-gray-300 px-2.5"
        >
          <SearchMdIcon class="h-4 w-4 shrink-0 text-gray-500" />
          <input
            id="conditional-product-search"
            v-model="searchTerm"
            type="text"
            class="min-w-0 flex-1 appearance-none border-0 bg-transparent p-0 text-sm text-gray-900 shadow-none placeholder:text-gray-500 focus:outline-none"
            :placeholder="t('form.conditonalLogic.searchProductsOrVariants')"
          />
        </label>
        <button
          id="conditional-product-select-all"
          type="button"
          class="shrink-0 cursor-pointer appearance-none border-0 bg-transparent p-0 text-sm font-semibold text-primary-600 shadow-none focus:outline-none"
          @click="onSelectAll"
        >
          {{ t("form.conditonalLogic.selectAll") }}
        </button>
        <button
          id="conditional-product-clear"
          type="button"
          class="shrink-0 cursor-pointer appearance-none border-0 bg-transparent p-0 text-sm font-semibold text-gray-600 shadow-none focus:outline-none"
          @click="onClear"
        >
          {{ t("form.conditonalLogic.clear") }}
        </button>
      </div>

      <div class="max-h-[264px] overflow-y-auto p-1 pb-2">
        <template v-if="visibleProducts.length">
          <button
            type="button"
            class="flex w-full cursor-pointer appearance-none items-center gap-x-2 rounded border-0 bg-transparent px-2 py-1.5 text-left shadow-none hover:bg-gray-50 focus:outline-none"
            @click="toggleGroupExpanded('main')"
          >
            <ChevronRightIcon
              class="h-3.5 w-3.5 shrink-0 text-gray-500 transition-transform"
              :class="expandedGroups.has('main') ? 'rotate-90' : ''"
            />
            <span class="text-sm font-semibold text-gray-700">
              {{ t("form.conditonalLogic.mainProduct") }}
            </span>
          </button>

          <template v-if="expandedGroups.has('main')">
            <div
              v-for="product in visibleProducts"
              :key="product.productId"
              class="pl-3"
            >
              <!--
                Expand and select are siblings, never nested: a control inside a
                role="checkbox" is not exposed to screen readers. Clicking anywhere on
                the row except the chevron selects, as the design specifies.
              -->
              <div
                class="flex h-9 items-center gap-x-2 rounded px-2 hover:bg-gray-50"
              >
                <button
                  v-if="hasVariantRows(product)"
                  :id="`conditional-product-expand-${product.productId}`"
                  type="button"
                  class="flex h-5 w-5 shrink-0 cursor-pointer appearance-none items-center justify-center rounded border-0 bg-transparent p-0 text-gray-500 shadow-none hover:bg-gray-100 focus:outline-none"
                  :aria-expanded="isProductExpanded(product)"
                  :aria-label="productName(product.name)"
                  @click="toggleProductExpanded(product)"
                >
                  <ChevronRightIcon
                    class="h-3.5 w-3.5 transition-transform"
                    :class="isProductExpanded(product) ? 'rotate-90' : ''"
                  />
                </button>
                <span v-else class="invisible h-5 w-5 shrink-0" />
                <button
                  :id="`conditional-product-${product.productId}`"
                  type="button"
                  role="checkbox"
                  :aria-checked="
                    productPartial(product) ? 'mixed' : productChecked(product)
                  "
                  class="flex min-w-0 flex-1 cursor-pointer appearance-none items-center gap-x-2 border-0 bg-transparent p-0 text-left shadow-none focus:outline-none"
                  @click="onProductToggle(product)"
                >
                  <TriStateCheckbox
                    :checked="productChecked(product)"
                    :indeterminate="productPartial(product)"
                  />
                  <span class="min-w-0 flex-1 truncate text-sm text-gray-900">
                    {{ productName(product.name) }}
                  </span>
                  <span class="shrink-0 text-[13px] text-gray-500">
                    {{ productMeta(product) }}
                  </span>
                </button>
              </div>

              <template v-if="isProductExpanded(product)">
                <div
                  v-for="variant in visibleVariants(product)"
                  :key="variant.priceId"
                  class="flex h-8 items-center rounded pl-9 pr-2 hover:bg-gray-50"
                >
                  <button
                    :id="`conditional-variant-${product.productId}-${variant.priceId}`"
                    type="button"
                    role="checkbox"
                    :aria-checked="variantChecked(product, variant.priceId)"
                    class="flex min-w-0 flex-1 cursor-pointer appearance-none items-center gap-x-2 border-0 bg-transparent p-0 text-left shadow-none focus:outline-none"
                    @click="onVariantToggle(product, variant.priceId)"
                  >
                    <TriStateCheckbox
                      :checked="variantChecked(product, variant.priceId)"
                    />
                    <span class="min-w-0 flex-1 truncate text-sm text-gray-700">
                      {{
                        variant.name || t("form.conditonalLogic.deletedVariant")
                      }}
                    </span>
                    <span class="shrink-0 text-[13px] text-gray-500">
                      {{ formatPrice(variant.amount, variant.currency) }}
                    </span>
                  </button>
                </div>
              </template>
            </div>
          </template>
        </template>

        <template v-if="visibleBumps.length">
          <button
            type="button"
            class="flex w-full cursor-pointer appearance-none items-center gap-x-2 rounded border-0 bg-transparent px-2 py-1.5 text-left shadow-none hover:bg-gray-50 focus:outline-none"
            @click="toggleGroupExpanded('bump')"
          >
            <ChevronRightIcon
              class="h-3.5 w-3.5 shrink-0 text-gray-500 transition-transform"
              :class="expandedGroups.has('bump') ? 'rotate-90' : ''"
            />
            <span class="text-sm font-semibold text-gray-700">
              {{ t("form.conditonalLogic.bumpProduct") }}
            </span>
          </button>

          <template v-if="expandedGroups.has('bump')">
            <div
              v-for="bump in visibleBumps"
              :key="variantSelectionId(bump.productId, bump.priceId)"
              class="ml-3 flex h-9 items-center gap-x-2 rounded px-2 hover:bg-gray-50"
            >
              <span class="invisible h-5 w-5 shrink-0" />
              <button
                :id="`conditional-bump-${bump.productId}-${bump.priceId}`"
                type="button"
                role="checkbox"
                :aria-checked="bumpChecked(bump)"
                class="flex min-w-0 flex-1 cursor-pointer appearance-none items-center gap-x-2 border-0 bg-transparent p-0 text-left shadow-none focus:outline-none"
                @click="onBumpToggle(bump)"
              >
                <TriStateCheckbox :checked="bumpChecked(bump)" />
                <span class="min-w-0 flex-1 truncate text-sm text-gray-900">
                  {{ productName(bump.name) }}
                </span>
                <span class="shrink-0 text-[13px] text-gray-500">
                  {{ formatPrice(bump.amount, bump.currency) }}
                </span>
              </button>
            </div>
          </template>
        </template>

        <p
          v-if="!hasResults"
          class="m-0 px-2 py-4 text-center text-[13px] text-gray-500"
        >
          {{ t("form.conditonalLogic.noProductsMatchSearch") }}
        </p>
      </div>

      <div
        class="flex items-center gap-x-2 border-t border-gray-200 px-3 py-2.5 text-[13px]"
        :class="
          isFooterWarning
            ? 'bg-warning-50 text-warning-700'
            : 'bg-white text-gray-600'
        "
      >
        <InfoCircleIcon class="h-[15px] w-[15px] shrink-0" />
        <span class="min-w-0 flex-1">{{ footerHint }}</span>
        <span class="shrink-0 font-semibold text-gray-700">
          {{
            t("form.conditonalLogic.productsSelectedCount", {
              selected: selectedCount,
              total: totalCount,
            })
          }}
        </span>
      </div>
    </template>
  </div>
</template>

<style scoped>
/* Preflight is off app-wide, so borders would inflate every box past its size class. */
.conditional-product-picker,
.conditional-product-picker *,
.conditional-product-picker *::before,
.conditional-product-picker *::after {
  box-sizing: border-box;
}
</style>
